import { GridColDef, GridSortModel, GridCellParams } from '@mui/x-data-grid';
import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DataGridModel, DataGrid } from 'src/shared/components/organisms/dataGrid';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { getAllWebForms } from 'src/shared/services/webFormService';
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from 'src/shared/models/enums/gridFilterConfig';
import { FundraiserSearchFilter } from '../../fundraiser/models/fundraiserSearchFilter';
import { WebForm } from '../models/webForm';

type WebFormsDataGridProps = {
  loadingCallback: (loading: boolean) => void;
  searchFilter: FundraiserSearchFilter;
  searchFilterCallback: (filter: FundraiserSearchFilter) => void;
};

export const WebFormsDataGrid = (props: WebFormsDataGridProps) => {
  const { loadingCallback, searchFilter, searchFilterCallback } = props;
  const { t } = useTranslation(['fundraiser']);
  const isMounted = useIsMounted();

  const navigate = useNavigate();

  const [rows, setRows] = useState<WebForm[]>([]);
  const [data, setData] = React.useState<DataGridModel>({
    loading: true,
    totalRows: 0,
    rowsPerPageOptions: PAGE_SIZE_OPTIONS,
    pageSize: DEFAULT_PAGE_SIZE,
    page: DEFAULT_PAGE_INDEX,
  });

  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: searchFilter.sortCol, sort: searchFilter.sortDir }]);

  const updateData = (k, v) => setData((prev) => ({ ...prev, [k]: v }));

  useEffect(() => {
    setData((prev) => ({ ...prev, ...{ page: searchFilter.pageIndex, pageSize: searchFilter.pageSize } }));
  }, [searchFilter]);

  const onPageChange = (newPageIndex: number) => {
    searchFilterCallback({ ...searchFilter, pageIndex: newPageIndex });
  };

  const onPageSizeChange = (newPageSize: number) => {
    searchFilterCallback({ ...searchFilter, pageIndex: 0, pageSize: newPageSize });
  };

  const fetchData = useCallback(async () => {
    if (isMounted()) {
      updateData('loading', true);
      loadingCallback(true);
    }

    try {
      const result = await getAllWebForms(searchFilter);
      if (isMounted()) {
        updateData('totalRows', result.data.totalItems);
        ConsoleHelper.log(result);
        setTimeout(() => {
          setRows(result.data.results);
        }, 100);
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
      }
    } finally {
      if (isMounted()) {
        updateData('loading', false);
        loadingCallback(false);
      }
    }
  }, [isMounted, loadingCallback, searchFilter]);

  useEffect(() => {
    if (isMounted()) {
      fetchData();
    }
  }, [searchFilter, fetchData, isMounted]);

  const onRowClick = (params) => {
    navigate(`${RoutesEnum.WebFormSettings}/${params.row.formName}`);
  };

  const columns: GridColDef[] = [
    {
      field: 'formName',
      headerName: t('fundraiser:tableHeader.name'),
      sortable: false,
      flex: 1,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.formName}</>;
      },
    },
    {
      field: 'formDescription',
      headerName: t('fundraiser:tableHeader.description'),
      sortable: false,
      flex: 2,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.formDescription}</>;
      },
    },
    {
      field: 'id',
      headerName: t('fundraiser:tableHeader.dateCreated'),
      sortable: true,
      flex: 1,
      renderCell: (params: GridCellParams) => {
        return <>{DateHelper.getDateTime(params.row.createdDate) || ''}</>;
      },
    },
  ];

  return (
    <Box
      sx={{
        position: 'relative',
        '& .MuiDataGrid-root--densityCompact .MuiDataGrid-cell': {
          py: 1,
        },
      }}
    >
      <DataGrid<WebForm>
        columns={columns}
        rows={rows}
        autoHeight
        columnVisibilityModel={{ id: false }}
        dataModel={data}
        checkboxSelection={false}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        sortModel={sortModel}
        onSortModelChange={(newSortModel) => {
          ConsoleHelper.log('newSortModel', newSortModel);
          const sort = sortModel[0].sort === 'asc' ? 'desc' : 'asc';
          if (newSortModel.length) {
            setSortModel(newSortModel);
          } else {
            setSortModel([{ field: sortModel[0].field, sort }]);
          }
          searchFilterCallback({
            ...searchFilter,
            pageIndex: 0,
            sortCol: newSortModel.length ? newSortModel[0].field : sortModel[0].field,
            sortDir: newSortModel.length ? newSortModel[0].sort : sort,
          });
        }}
        onRowClick={onRowClick}
        selectionModel={[]}
        onSelectionModelChange={() => ConsoleHelper.log('')}
      />
    </Box>
  );
};
